import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
  },
  timeout: 15000,
});



// Add response interceptor for debugging
api.interceptors.response.use(
  response => {
    // console.log('✓ Axios response received:', response.status, response.data);
    return response;
  },
  error => {
    if (error.response) {
      // Server responded with error
      console.error('✗ Axios error:', error.response.status, error.response.data);
      
      // Handle specific error cases
      if (error.response.status === 401) {
        console.error('🔒 Authentication failed - Invalid admin password');
      } else if (error.response.status === 403) {
        console.error('🚫 Forbidden - Access denied');
      } else if (error.response.status === 500) {
        console.error('💥 Server error');
      }
    } else if (error.request) {
      // Request made but no response
      console.error('✗ Axios error: Network Error', error.message);
    } else {
      // Something else happened
      console.error('✗ Axios error:', error.message);
    }
    return Promise.reject(error);
  }
);

// Place API calls
export const placeService = {
  // Create a new place/pin
  create: async (placeData) => {
    const response = await api.post('/places', placeData);
    return response.data;
  },

  // Create a new place with image
  createWithImage: async (formData) => {
    const response = await api.post('/places', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Get nearby places
  getNearby: async (lat, lng, radius = 5) => {
    const response = await api.get('/places/nearby', {
      params: { lat, lng, radius },
    });
    return response.data;
  },

  // Get all places
  getAll: async () => {
    const response = await api.get('/places/all');
    return response.data;
  },

  // Get a single place
  get: async (id) => {
    const response = await api.get(`/places/${id}`);
    return response.data;
  },

  // Update a place
  update: async (id, placeData) => {
    const response = await api.put(`/places/${id}`, placeData);
    return response.data;
  },

  // Delete a place
  delete: async (id) => {
    const response = await api.delete(`/places/${id}`);
    return response.data;
  },

  // Like a place
  like: async (id, sessionId) => {
    const response = await api.post(`/places/${id}/like`, { sessionId });
    return response.data;
  },

  // Vote to delete a place
  voteDelete: async (id, sessionId) => {
    const response = await api.post(`/places/${id}/vote-delete`, { sessionId });
    return response.data;
  },

  // Get comments for a place
  getComments: async (id) => {
    const response = await api.get(`/places/${id}/comments`);
    return response.data;
  },

  // Add a comment to a place
  addComment: async (id, content, sessionId) => {
    const response = await api.post(`/places/${id}/comments`, { content, sessionId });
    return response.data;
  },
};

// Report API calls
export const reportService = {
  // Create a report
  create: async (reportData) => {
    const response = await api.post('/reports', reportData);
    return response.data;
  },

  // Get all reports (admin) - requires password
  getAll: async (adminPassword) => {
    const response = await api.get('/reports', {
      headers: { 'x-admin-password': adminPassword }
    });
    return response.data;
  },

  // Approve a report (admin) - requires password
  approve: async (id, adminPassword) => {
    const response = await api.post(`/reports/${id}/approve`, {}, {
      headers: { 'x-admin-password': adminPassword }
    });
    return response.data;
  },

  // Reject a report (admin) - requires password
  reject: async (id, adminPassword) => {
    const response = await api.post(`/reports/${id}/reject`, {}, {
      headers: { 'x-admin-password': adminPassword }
    });
    return response.data;
  },
};

// Config API calls
export const configService = {
  // Get configuration - requires password
  get: async (adminPassword) => {
    const response = await api.get('/config', {
      headers: { 'x-admin-password': adminPassword }
    });
    return response.data;
  },

  // Update configuration (admin) - requires password
  update: async (configData, adminPassword) => {
    const response = await api.put('/config', configData, {
      headers: { 'x-admin-password': adminPassword }
    });
    return response.data;
  },
};

export default api;
